import React, { useState, useEffect } from 'react'
import AdminLayout from '../../../layouts/AdminLayout';
import { IconPlus } from '@tabler/icons-react';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import SearchComponent from '../components/SearchComponent';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import StockCategoriesTable from '../components/StockCategoriesTable';
import StockItemsTable from '../components/StockItemsTable';
import { useStockData } from '../hooks/useStockData';
import CreateCategoryDialog from '../components/CreateCategoryDialog';
import EditCategoryDialog from '../components/EditCategoryDialog';
import CreateStockItemDialog from '../components/CreateStockItemDialog';
import EditStockItemDialog from '../components/EditStockItemDialog';
import { Category, StockItem } from '../types';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

const StockInventoryPage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(10); // Fixed items per page

    // Dialog states
    const [showCreateCategory, setShowCreateCategory] = useState<boolean>(false);
    const [showEditCategory, setShowEditCategory] = useState<boolean>(false);
    const [showCreateItem, setShowCreateItem] = useState<boolean>(false);
    const [showEditItem, setShowEditItem] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);

    // Use the custom hook for data management
    const {
        categories,
        stockItems,
        totalCategories,
        totalItems,
        totalCategoriesPages,
        totalItemsPages,
    } = useStockData(searchQuery, currentPage, itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Reset to page 1 when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handleAddCategory = () => {
        setShowCreateCategory(true);
    };

    const handleAddItem = () => {
        setShowCreateItem(true);
    };

    const handleEditCategory = (id: string) => {
        // Find the category by id
        const category = categories.find(cat => cat.id.toString() === id);
        if (category) {
            setSelectedCategory(category);
            setShowEditCategory(true);
        }
    };

    const handleEditItem = (id: string) => {
        // Find the item by id
        const item = stockItems.find(it => it.id.toString() === id);
        if (item) {
            setSelectedItem(item);
            setShowEditItem(true);
        }
    };

    const handleDeleteCategory = (id: string) => {
        // TODO: Implement delete category with confirmation
        console.log('Delete category:', id);
    };

    const handleDeleteItem = (id: string) => {
        // TODO: Implement delete item with confirmation
        console.log('Delete item:', id);
    };

    const handleCloseCreateCategory = () => {
        setShowCreateCategory(false);
    };

    const handleCloseEditCategory = () => {
        setShowEditCategory(false);
        setSelectedCategory(null);
    };

    const handleCloseCreateItem = () => {
        setShowCreateItem(false);
    };

    const handleCloseEditItem = () => {
        setShowEditItem(false);
        setSelectedItem(null);
    };

    const handleSuccess = () => {
        // TODO: Refresh data after successful operation
        console.log('Operation successful, refresh data');
    };

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container min-vh-100">
                    <h3 className="mb-3">Stock Inventory</h3>
                    <PropertyInactiveBanner />

                    {/* Search Section */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                        <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                        <div>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-primary me-2" onClick={handleAddCategory}>
                                        <IconPlus size={16} className="me-1" />
                                        Add Category
                                    </button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-success" onClick={handleAddItem}>
                                        <IconPlus size={16} className="me-1" />
                                        Add Item
                                    </button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>
                    </div>

                    {/* Categories Section */}
                    <div className="mb-5">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                            <div>
                                <h5 className="mb-0">Stock Categories</h5>
                                <small className="text-muted">Manage your stock categories ({totalCategories} total)</small>
                            </div>
                        </div>

                        <div className="stock-categories-wrapper">
                            <StockCategoriesTable
                                categories={categories}
                                onEdit={handleEditCategory}
                                onDelete={handleDeleteCategory}
                            />
                        </div>

                        {/* Pagination for Categories */}
                        {totalCategoriesPages > 1 && (
                            <PaginationComponent
                                totalPages={totalCategoriesPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>

                    {/* Items Section */}
                    <div className="mb-5">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                            <div>
                                <h5 className="mb-0">Stock Items</h5>
                                <small className="text-muted">Manage your stock items ({totalItems} total)</small>
                            </div>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-success" onClick={handleAddItem}>
                                        <IconPlus size={16} className="me-1" />
                                        Add Item
                                    </button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>

                        <div className="stock-items-wrapper">
                            <StockItemsTable
                                items={stockItems}
                                onEdit={handleEditItem}
                                onDelete={handleDeleteItem}
                            />
                        </div>

                        {/* Pagination for Items */}
                        {totalItemsPages > 1 && (
                            <PaginationComponent
                                totalPages={totalItemsPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Dialogs */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateCategoryDialog
                        show={showCreateCategory}
                        onClose={handleCloseCreateCategory}
                        onSuccess={handleSuccess}
                    />
                </ReadOnlyGuard>
            </RequireAuth>

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <EditCategoryDialog
                        show={showEditCategory}
                        onClose={handleCloseEditCategory}
                        category={selectedCategory}
                        onSuccess={handleSuccess}
                    />
                </ReadOnlyGuard>
            </RequireAuth>

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateStockItemDialog
                        show={showCreateItem}
                        onClose={handleCloseCreateItem}
                        onSuccess={handleSuccess}
                    />
                </ReadOnlyGuard>
            </RequireAuth>

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['stock_inventory_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <EditStockItemDialog
                        show={showEditItem}
                        onClose={handleCloseEditItem}
                        item={selectedItem}
                        onSuccess={handleSuccess}
                    />
                </ReadOnlyGuard>
            </RequireAuth>
        </AdminLayout>
    )
}

export default StockInventoryPage